<!-- src/components/PhoneNumberDialog.vue -->
<template>
  <v-dialog v-model="dialog" persistent max-width="400px">
    <v-card>
      <v-card-title>
        <span class="headline">We will call you. What is your phone number?</span>
      </v-card-title>
      <v-card-text>
        <v-text-field
          v-model="phoneNumber"
          :placeholder="placeholder"
          :error="hasError"
          outlined
          dense
          @keydown.enter="handleOk"
          @keydown.esc="handleCancel"
          ref="phoneInput"
        ></v-text-field>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="handleCancel">
          Cancel
        </v-btn>
        <v-btn color="primary" @click="handleOk">
          OK
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PhoneNumberDialog',
  
  props: {
    value: {  // Back to 'value'
      type: Boolean,
      default: false
    },
    currentPhoneNumber: {
      type: String,
      default: ''
    },
    userUid: {
      type: String,
      required: true
    }
  },

  emits: ['input', 'phone-number-confirmed', 'canceled'],  // Declare emits

  data() {
    return {
      phoneNumber: '',
      hasError: false,
      placeholder: '###-###-####'
    };
  },

  computed: {
    dialog: {
      get() {
        return this.value;  // Back to this.value
      },
      set(value) {
        this.$emit('input', value);  // Back to 'input'
      }
    }
  },

  watch: {
    value(newVal) {  // Back to 'value'
      if (newVal) {
        console.log('🔵 Dialog opening, currentPhoneNumber:', this.currentPhoneNumber);
        // Dialog opened - convert +1 format back to ###-###-#### format for display
        this.phoneNumber = this.formatForDisplay(this.currentPhoneNumber);
        console.log('🔵 Formatted phone number for display:', this.phoneNumber);
        this.hasError = false;
        this.$nextTick(() => {
          setTimeout(() => {  // Added setTimeout for better timing
            if (this.$refs.phoneInput) {
              this.$refs.phoneInput.focus();
            }
          }, 100);
        });
      }
    }
  },

  methods: {
    extractDigits(input) {
      // Extract only digits from the input
      return input.replace(/\D/g, '');
    },

    isValidPhone(input) {
      // Check if input contains exactly 10 digits
      const digits = this.extractDigits(input);
      return digits.length === 10;
    },

    formatToE164(input) {
      // Convert to +1########## format
      const digits = this.extractDigits(input);
      return `+1${digits}`;
    },

    formatForDisplay(phoneNumber) {
      // Convert +1########## back to ###-###-#### format for display
      console.log('🔵 formatForDisplay input:', phoneNumber);
      
      if (!phoneNumber) return '';
      
      const digits = this.extractDigits(phoneNumber);
      console.log('🔵 Extracted digits:', digits);
      
      if (digits.length === 11 && digits.startsWith('1')) {
        // Remove the leading 1 and format as ###-###-####
        const tenDigits = digits.substring(1);
        const formatted = `${tenDigits.substring(0, 3)}-${tenDigits.substring(3, 6)}-${tenDigits.substring(6, 10)}`;
        console.log('🔵 Formatted (11 digits):', formatted);
        return formatted;
      } else if (digits.length === 10) {
        // Already 10 digits, format as ###-###-####
        const formatted = `${digits.substring(0, 3)}-${digits.substring(3, 6)}-${digits.substring(6, 10)}`;
        console.log('🔵 Formatted (10 digits):', formatted);
        return formatted;
      }
      
      // Return as-is if not in expected format
      console.log('🔵 Returning as-is:', phoneNumber);
      return phoneNumber;
    },

    async handleOk() {
      console.log('🔵 handleOk called');
      
      if (!this.isValidPhone(this.phoneNumber)) {
        this.hasError = true;
        return;
      }

      this.hasError = false;
      const formattedNumber = this.formatToE164(this.phoneNumber);
      
      console.log('🔵 About to emit events and close dialog');
      
      // Update database if different from current
      if (formattedNumber !== this.currentPhoneNumber) {
        try {
          await axios.put(`/portaldb/db/updateUserProperties/${this.userUid}`, {
            personalPhoneNumber: formattedNumber
          });
        } catch (error) {
          console.error('Failed to update phone number:', error);
          // Continue anyway - we don't want to block the user
        }
      }

      // Emit the formatted phone number and close dialog
      this.$emit('phone-number-confirmed', formattedNumber);
      this.$emit('input', false);  // Close the dialog
      
      console.log('🔵 Events emitted');
    },

    handleCancel() {
      console.log('🔵 handleCancel called');
      this.hasError = false;
      this.$emit('canceled');
      this.$emit('input', false);  // Close the dialog
      console.log('🔵 Cancel events emitted');
    }
  }
};
</script>

<style scoped>
.headline {
  font-size: 1.1rem !important;
  line-height: 1.3;
}
</style>
